/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/B2BEventParticipatingCompanyModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useEffect, useRef } from "react";

import styles from "./index.module.css";
import B2BEventParticipatingCompanyForm from "../../components/B2BEventParticipatingCompanyForm/index.jsx";

/**
 * Desktop modal wrapper for add/edit participating company. Uses native `<dialog>`;
 * tablet/mobile should use `B2BEventParticipatingCompanyView` instead.
 *
 * @param {Object} props — Same as `B2BEventParticipatingCompanyForm` plus `open`.
 * @param {boolean} props.open
 * @param {function} props.onCancel
 * @param {function} props.onSuccess
 */
const B2BEventParticipatingCompanyModal = ({
  open,
  onCancel,
  onSuccess,
  ...formProps
}) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return undefined;
    }

    const handleClose = () => {
      onCancel();
    };

    dialog.addEventListener("close", handleClose);

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    } else {
      // Dialog already matches the desired open state
    }

    return () => {
      dialog.removeEventListener("close", handleClose);
    };
  }, [open, onCancel]);

  return (
    <>
      {open && <div className={styles.overlay} />}
      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.closeButtonContainer}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onCancel}
          >
            &times;
          </button>
        </div>
        {open && (
          <B2BEventParticipatingCompanyForm
            active={open}
            variant="modal"
            onCancel={onCancel}
            onSuccess={onSuccess}
            {...formProps}
          />
        )}
      </dialog>
    </>
  );
};

export default B2BEventParticipatingCompanyModal;
